import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { VERTICALS } from '../config/verticals';
import { CONTACT_EMAIL, CONTACT_LOCATION, CONTACT_PHONE } from '../config/legal';

export const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-black text-white">
      {/* CTA Section */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-3">
                Ready to drive?
              </h2>
              <p className="text-gray-400 text-lg max-w-md">
                See how much you can get in under 2 minutes.
              </p>
            </div>
            <Link to="/get-started">
              <Button
                data-testid="footer-cta-btn"
                className="bg-[#00D47E] text-black border-2 border-[#00D47E] hover:bg-transparent hover:text-white hover:border-white rounded-full px-8 py-6 text-lg font-medium transition-all"
              >
                Check My Eligibility
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img 
                src="/levr-logo.png" 
                alt="Levr" 
                className="w-40 md:w-44 h-auto object-contain"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              Helping Kiwis find the best vehicle finance deals.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-outfit font-semibold text-sm uppercase tracking-wider text-[#00D47E] mb-4">
              Quick Links
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/get-started" className="text-gray-300 hover:text-[#00D47E] transition-colors text-sm">
                  Check My Eligibility
                </Link>
              </li>
              <li>
                <Link to="/calculator" className="text-gray-300 hover:text-[#00D47E] transition-colors text-sm">
                  Repayment Calculator
                </Link>
              </li>
              <li>
                <Link to="/pre-approval" className="text-gray-300 hover:text-[#00D47E] transition-colors text-sm">
                  Pre-Approval Explained
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-gray-300 hover:text-[#00D47E] transition-colors text-sm">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-300 hover:text-[#00D47E] transition-colors text-sm">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/disclaimer" className="text-gray-300 hover:text-[#00D47E] transition-colors text-sm">
                  Disclaimer
                </Link>
              </li>
            </ul>
          </div>

          {/* Finance For — the vertical landing pages.
              Mapped from the shared config rather than hand-listed, so adding a vertical adds its
              footer link automatically. These are the site's only internal links to those pages:
              without them the pages are reachable but not discoverable, by a visitor or a crawler.
              The footer renders on every page including the verticals themselves, so each one links
              to the other five. */}
          <div>
            <h4 className="font-outfit font-semibold text-sm uppercase tracking-wider text-[#00D47E] mb-4">
              Finance For
            </h4>
            <ul className="space-y-3">
              {VERTICALS.map((v) => (
                <li key={v.slug}>
                  <Link
                    to={`/finance/${v.slug}`}
                    className="text-gray-300 hover:text-[#00D47E] transition-colors text-sm"
                  >
                    {v.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Lenders — described, not named. Same reasoning as the landing page section.
              Heading was "Our Lenders" until 2026-08-26; the possessive implied an accredited panel
              Levr has not confirmed. The copy below names nobody and claims no count, which is what
              a broker can say without substantiating a panel. */}
          <div>
            <h4 className="font-outfit font-semibold text-sm uppercase tracking-wider text-[#00D47E] mb-4">
              How We Work
            </h4>
            <p className="text-sm text-gray-300 leading-relaxed">
              We work with established New Zealand banks and finance companies, held to
              responsible lending standards.
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mt-3">
              A specialist matches you to the ones that suit your situation.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-outfit font-semibold text-sm uppercase tracking-wider text-[#00D47E] mb-4">
              Contact Us
            </h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-sm text-gray-300">
                <Mail className="w-4 h-4 text-[#00D47E]" />
                {CONTACT_EMAIL}
              </li>
              {/* Rendered only when a real number is configured — see config/legal.js. */}
              {CONTACT_PHONE ? (
                <li className="flex items-center gap-2 text-sm text-gray-300">
                  <Phone className="w-4 h-4 text-[#00D47E]" />
                  {CONTACT_PHONE}
                </li>
              ) : null}
              <li className="flex items-center gap-2 text-sm text-gray-300">
                <MapPin className="w-4 h-4 text-[#00D47E]" />
                {CONTACT_LOCATION}
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} Levr. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500">
            <Link to="/privacy" className="hover:text-[#00D47E] transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#00D47E] transition-colors">Terms of Service</Link>
            <Link to="/disclaimer" className="hover:text-[#00D47E] transition-colors">Disclaimer</Link>
            {/* Agency credit, matching the other Webgun client sites. `noopener` WITHOUT
                `noreferrer` on purpose, so webgun.nz still sees the referral in its analytics. */}
            <a
              href="https://webgun.nz/"
              target="_blank"
              rel="noopener"
              className="whitespace-nowrap hover:text-[#00D47E] transition-colors"
            >
              Built by Webgun
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
